import { Link } from "@/lib/router-compat";
import { SearchIcon, WrenchIcon } from "@/components/icons/InlineIcons";
import { Button } from "@/components/ui/button";
import { useHeroContent } from "@/hooks/useHeroContent";
import { useEffect, useState } from "react";
import HeroButtons from "@/components/ui/HeroButtons";
import { homepageHeroMobileSrc } from "@/lib/heroPreload";
import { useInteractionReady } from "@/components/ui/InteractionGate";

/** Mobile-only CMS hero. The route loader puts the live first slide in SSR
 * markup, so mobile paints one real LCP image rather than a temporary shell. */
export default function MobileHeroStrip() {
  const { data: cms } = useHeroContent("home", "hero_slides");
  const slides = cms?.slides?.filter((s) => s.image_mobile || s.image_desktop) ?? [];
  const hasCms = slides.length > 0;
  const [idx, setIdx] = useState(0);
  const engaged = useInteractionReady();

  const autoplay = cms?.layout?.autoplay !== false;
  const interval = cms?.layout?.interval_ms ?? 6000;

  useEffect(() => {
    if (!engaged || window.matchMedia("(prefers-reduced-motion: reduce)").matches || !hasCms || slides.length <= 1 || !autoplay) return;
    let intervalId: ReturnType<typeof setInterval> | undefined;
    const start = () => {
      if (intervalId) return;
      intervalId = setInterval(() => {
        setIdx((current) => {
          const next = (current + 1) % slides.length;
          const nextSlide = slides[next];
          const nextSrc = nextSlide?.image_mobile || nextSlide?.image_desktop;
          const nextRendered = nextSrc ? homepageHeroMobileSrc(nextSrc) ?? nextSrc : undefined;
          if (nextRendered) {
            const preload = new Image();
            preload.src = nextRendered;
            void preload.decode().then(() => setIdx(next)).catch(() => setIdx(next));
          }
          return current;
        });
      }, interval);
    };
    start();
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [engaged, hasCms, slides.length, autoplay, interval]);

  if (hasCms) {
    const slide = slides[idx % slides.length];
    if (!slide) return null;
    const img = slide.image_mobile || slide.image_desktop;
    // Transform through the render pipeline (828w / q45) so the LCP image is
    // ~40% smaller than the raw storage object and matches the SSR preload.
    const src = homepageHeroMobileSrc(img) ?? img;
    return (
      <section className="relative h-[200px] w-full overflow-hidden bg-secondary" aria-label="Rex & Sons RVs">
        <h1 className="sr-only">Rex & Sons RVs</h1>
        <img
          key={src}
          src={src}
          alt={slide.title || ""}
          width={828}
          height={200}
          className="absolute inset-0 h-full w-full object-cover"
          loading={idx === 0 ? "eager" : "lazy"}
          decoding={idx === 0 ? "sync" : "async"}
          fetchPriority={idx === 0 ? "high" : "low"}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/65" />
        <div className="relative z-10 flex h-full flex-col items-center justify-center px-4 text-center">
          {slide.title && (
            <h2 className="font-heading text-2xl font-black uppercase leading-[28px] min-h-[28px] tracking-wide text-white drop-shadow">
              {slide.title}
            </h2>
          )}
          {slide.subtitle && (
            <p className="mt-1 text-xs text-white/90 drop-shadow line-clamp-2">
              {slide.subtitle}
            </p>
          )}
          <div className="mt-3">
            <HeroButtons
              {...(slide.buttons !== undefined ? { buttons: slide.buttons } : {})}
              fallback={
                <div className="flex gap-2">
                  <Button asChild size="sm" className="font-heading uppercase tracking-wide">
                    <Link to="/rv-search">
                      <SearchIcon className="mr-1.5 h-3.5 w-3.5" />
                      Browse Inventory
                    </Link>
                  </Button>
                  <Button asChild size="sm" variant="secondary" className="font-heading uppercase tracking-wide">
                    <Link to="/rv-service">
                      <WrenchIcon className="mr-1.5 h-3.5 w-3.5" />
                      Service
                    </Link>
                  </Button>
                </div>
              }
            />
          </div>
        </div>
      </section>
    );
  }

  // Preserve the hero slot if the CMS request fails without downloading an
  // unrelated fallback image that can compete with the real LCP candidate.
  return (
    <section className="flex h-[200px] w-full items-center justify-center bg-secondary px-4 text-center" aria-label="Rex & Sons RVs">
      <h1 className="font-heading text-2xl font-black uppercase text-secondary-foreground">
        Rex &amp; Sons RVs
      </h1>
    </section>
  );
}